import type { Website } from 'types';

export type TrackingMethodChoice = NonNullable<Website['trackingMethod']> | 'none';

type TrackingMethodOption = { value: TrackingMethodChoice; label: string; description: string };

/**
 * The tracking-method choices, shared by the settings dialog on the websites list and the
 * website details screen so the two cannot offer different things.
 *
 * 'server' is the one-reporter rule made a setting: the browser runtime reports nothing, and
 * /sdk/events (refused in every other mode) is how conversions arrive. A Shopify store cannot
 * use it, since its checkout pixel reports from the browser.
 */
export const TRACKING_METHOD_OPTIONS: readonly TrackingMethodOption[] = [
  { value: 'ga4', label: 'GA4', description: 'Google Analytics 4 integration' },
  { value: 'matomo', label: 'Matomo', description: 'Matomo Analytics integration' },
  { value: 'pertento', label: 'Pertento', description: 'Pertento native tracking' },
  {
    value: 'server',
    label: 'Server-side',
    description:
      'Conversions are reported only by your backend, through the SDK or /sdk/events. The browser reports nothing.',
  },
  { value: 'none', label: 'Not selected', description: 'No tracking method configured' },
];

export type TrackingMethodOffer = TrackingMethodOption & {
  /** Shown because it is the website's current method, but not one this user may choose. */
  readOnly: boolean;
};

type TrackingMethodOptionsParams = {
  /** Whether this user may choose Server-side: canUseServerSide, staff-only for now (PER-178). */
  allowServer: boolean;
  current: Website['trackingMethod'] | undefined;
};

/**
 * The choices one user is offered. Server-side is left out for someone who may not use
 * server-side yet, except on a website staff already put on it: there it is still shown,
 * selected and read-only, so the form says what the website does and saving another field
 * re-sends it unchanged (which the API allows). Choosing another method stays open.
 */
export function trackingMethodOptions({ allowServer, current }: TrackingMethodOptionsParams): TrackingMethodOffer[] {
  return TRACKING_METHOD_OPTIONS.filter(
    (option) => option.value !== 'server' || allowServer || current === 'server',
  ).map((option) => ({ ...option, readOnly: option.value === 'server' && !allowServer }));
}

/** The short name a website row shows for its tracking method. */
export const TRACKING_LABEL: Record<NonNullable<Website['trackingMethod']>, string> = {
  ga4: 'GA4',
  matomo: 'Matomo',
  pertento: 'Pertento',
  server: 'Server-side',
};

/** The confirm shown when Server-side is chosen for a website with no active server key. */
export const SERVER_TRACKING_CONFIRM = {
  title: 'Report conversions only from your backend?',
  body: [
    'This website has no active server key yet. With server-side tracking, the browser stops reporting conversions, so none are recorded for any experiment on this website until your backend reports them through the SDK or /sdk/events.',
    'Create a key under Server keys and deploy the backend code first, or choose this once it is live.',
  ],
  confirm: 'Use server-side tracking',
} as const;
